"use strict";
// Shared link-builder for the customer_document_upload micro-frontend.
//
// Used by both the captain `/customer-document-upload/link` route (audit-logged,
// authenticated entry point for agent UIs) AND any front-end that wants to
// construct the link locally without an HTTP round-trip (e.g. lighthouse
// embedding the URL inside an error popup that may render many times).
//
// Pure: no I/O, no config reads. `baseUrl` is provided by the caller — the
// backend resolves from `captain.frontend` config, the FE typically derives
// from `window.location.origin + CUSTOMER_DOCUMENT_UPLOAD_SPA_PATH`.
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildCustomerDocumentUploadLink = exports.validateCustomerDocumentUploadLinkParams = exports.CUSTOMER_DOCUMENT_UPLOAD_SPA_PATH = void 0;
exports.CUSTOMER_DOCUMENT_UPLOAD_SPA_PATH = '/customer_document_upload/';
// Normalizes a possibly-repeated / possibly-comma-separated value into a single
// comma-separated string. Returns undefined when there's nothing usable.
const toCsv = (v) => {
    const collect = (raw) => {
        if (typeof raw === 'string')
            return raw.split(',').map((s) => s.trim()).filter(Boolean);
        if (Array.isArray(raw))
            return raw.flatMap(collect);
        return [];
    };
    const parts = collect(v);
    return parts.length ? parts.join(',') : undefined;
};
const validateCustomerDocumentUploadLinkParams = (params) => {
    const { applicationId, policyId } = params;
    if (applicationId && policyId) {
        return { ok: false, code: 'INVALID_PARAMS', message: 'Provide exactly one of applicationId or policyId, not both' };
    }
    if (!applicationId && !policyId) {
        return { ok: false, code: 'MISSING_IDENTIFIER', message: 'One of applicationId or policyId is required' };
    }
    return { ok: true };
};
exports.validateCustomerDocumentUploadLinkParams = validateCustomerDocumentUploadLinkParams;
// Builds the deep link for the customer_document_upload micro-frontend.
//
//   baseUrl  — protocol + host + SPA path, e.g.
//              "https://app.ahoy.insure/customer_document_upload/" or
//              "http://localhost:8004/customer_document_upload/"
//   params   — applicationId XOR policyId required; documentType optional.
//
// Returns either { ok: true, link } or { ok: false, code, message } — never
// throws. The frontend can route the code to UX (e.g. an error toast) and the
// backend can map it to an HTTP status / Result.Error payload.
const buildCustomerDocumentUploadLink = (baseUrl, params) => {
    const validation = (0, exports.validateCustomerDocumentUploadLinkParams)(params);
    if (!validation.ok)
        return validation;
    const { applicationId, policyId, documentType } = params;
    const documentTypeCsv = toCsv(documentType);
    // URLSearchParams URL-encodes every value automatically; Vue Router on the
    // receiving side URL-decodes via `route.query`, so the FE doesn't need any
    // extra decoding step.
    const searchParams = new URLSearchParams();
    if (applicationId)
        searchParams.set('applicationId', applicationId);
    if (policyId)
        searchParams.set('policyId', policyId);
    if (documentTypeCsv)
        searchParams.set('document_type', documentTypeCsv);
    // The micro-frontend uses hash routing — params live after `#/` so Vue
    // Router picks them up via route.query. Tolerate baseUrl with or without a
    // trailing slash so callers don't have to normalize.
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const link = `${normalizedBase}#/?${searchParams.toString()}`;
    return { ok: true, link };
};
exports.buildCustomerDocumentUploadLink = buildCustomerDocumentUploadLink;
